import React from 'react';
import styled, { css } from 'styled-components';
import { AnimateIn, Container, TitleSet, media, fontSizes, ExternalLink, InternalLink } from '../../ui-components';
import renderRichText from '../../utils/render-rich-text';
import renderRichTextPreview from '../../utils/render-rich-text-preview';
import { isInternalLink } from '../../utils/link-handler';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import Video from '../video';
import Statistic from "../../ui-components/components/statistic";

/**
 * Component that renders ImageWithTitleset
 *
 * @component ImageWithTitleset
 * 
 * @param {object} props
 * 
 * @prop {object} contentBlock
 * @prop {boolean} centerTextSection
 * @prop {boolean} altMobileOrder
 * @prop {string} buttonText
 * @prop {string} buttonCTA
 * @prop {boolean} primaryButtonOnly
 * @prop {string} geoSpace
 * @prop {boolean} editorial
 * @prop {boolean} isPreview
 * @prop {string} objectFit
 * @prop {number} mobileGap
 * @prop {boolean} layoutStatistics
 * @prop {number} height
 * 
 * @returns {JSX}
 */
const ImageWithTitleset = props => {
  const {
    contentBlock,
    centerTextSection,
    altMobileOrder,
    buttonText,
    buttonCTA,
    primaryButtonOnly,
    geoSpace,
    editorial,
    isPreview,
    objectFit = 'cover',
    mobileGap = 40,
    landscape = false,
    layoutStatistics,
    height
  } = props;

  const imageLeft = props?.imageLeft ? props.imageLeft : contentBlock?.imageLeft;

  // Uses renderRichTextPreview function for Contentful Assessment previews
  if (isPreview) {
    contentBlock.subtitle = contentBlock.bodyRichJSON.map(section => {
      return renderRichTextPreview(section);
    });
  }

  const renderTextSection = () => {
    return (
      <TextSectionContainer layoutStatistics={layoutStatistics}>
        <TitleSet
          label={contentBlock?.label}
          title={contentBlock?.title}
          bodyText={
            contentBlock?.bodyRichJSON && !isPreview
              ? renderRichText(contentBlock?.bodyRichJSON, geoSpace)
              : contentBlock?.bodyString
          }
          alignLeft
        />
        {contentBlock?.items &&
          contentBlock.items.map((item, index )=> {
            return <Statistic key={index} stat={item?.stat} statContent={item?.statContent} largeMobileStat />;
          })}
        {!buttonCTA &&
          contentBlock?.ctaUrl &&
          (isInternalLink(contentBlock?.ctaUrl) ? (
            <CTALinkInternal withArrow hoverUnderline to={contentBlock.ctaUrl}>
              {contentBlock.ctaText || 'Explore Further'}
            </CTALinkInternal>
          ) : (
            <CTALink withArrow hoverUnderline href={contentBlock.ctaUrl}>
              {contentBlock.ctaText || 'Explore Further'}
            </CTALink>
          ))}
        {buttonCTA && buttonText && (
          <ButtonContainer>
            <FooterButton linkstyle="primary-button" to={buttonCTA}>
              {buttonText}
            </FooterButton>
            {!primaryButtonOnly && (
              <FooterButton linkstyle="secondary-button" to={contentBlock.ctaUrl}>
                {contentBlock.ctaText}
              </FooterButton>
            )}
          </ButtonContainer>
        )}
      </TextSectionContainer>
    );
  };

  const renderImageSection = () => {
    const gatsbyImage = getImage(contentBlock?.image?.fluid);

    if (contentBlock?.video) {
      return (
        <VideoContainer altMobileOrder={altMobileOrder} landscape={landscape || contentBlock?.videoLandscape}>
          <POVVideo embedUrl={contentBlock.video.embedUrl} title={contentBlock?.title} landscape={landscape || contentBlock?.videoLandscape}/>
          <VideoDescription>{contentBlock.video?.videoDescription}</VideoDescription>
        </VideoContainer>
      );
    } else {
      return (
        <ImageContainer editorial={editorial} layoutStatistics={layoutStatistics} >
          {isPreview ? (
            <ImagePreview src={contentBlock?.image?.fields?.file?.url} alt={contentBlock?.title} />
          ) : contentBlock?.image?.fluid?.images?.sources[1]?.type === 'image/gif' ? (
            <ImgContainer
              src={contentBlock?.image?.fluid?.images?.sources[0]?.srcSet.split('?')[0]}
              alt={contentBlock?.title}
            />
          ) : (
            gatsbyImage && <StyledGatsbyImage
              image={gatsbyImage}
              objectFit={objectFit}
              editorial={editorial}
              alt={contentBlock?.title}
              layoutStatistics={layoutStatistics}
              height={height}
            />
          )}
        </ImageContainer>
      );
    }
  };

  return (
    <AnimateIn onScroll>
      <SectionContainer
        id='image-with-title-set-container'
        sectionContainer
        centerTextSection={centerTextSection}
        altMobileOrder={altMobileOrder}
        mobileGap={mobileGap}
        editorial={editorial}
        layoutStatistics={layoutStatistics}
        landscape={landscape}
      >
        {imageLeft ? renderImageSection() : renderTextSection(geoSpace)}
        {imageLeft ? renderTextSection(geoSpace) : renderImageSection()}
      </SectionContainer>
    </AnimateIn>
  );
};

const VideoContainer = styled.div`
  width: ${({ landscape }) => (landscape ? `` : '500px')};

  @media screen and (max-width: ${media.mediumTop}) {
    display: flex;
    justify-content: center;
    max-width: 100%;
    margin-bottom: 20px;
  }
`;

const POVVideo = styled(Video)`
  width: ${({ landscape }) => (landscape ? `745px` : '500px')};
  height: ${({ landscape }) => (landscape ? `420.49px` : '500px')};
  margin: 0px;

  @media screen and (max-width: ${media.mediumTop}) {
    min-width: 335px;
    height: ${({ landscape }) => (landscape ? `189px` : '335px')};
    margin-bottom: -20px;
  }
`;

const VideoDescription = styled.p`
  padding-top: 12px;
  font-size: 16px;
  font-style: italic;
  line-height: 26px;
  max-width: ${({ landscape }) => (landscape ? `745px` : '500px')};

  @media screen and (max-width: ${media.mediumTop}) {
    display: none;
  }
`;

const SectionContainer = styled(Container)`
  display: flex;
  flex-direction: row;
  align-items: ${({ centerTextSection }) => (centerTextSection ? 'center' : 'flex-start')};
  justify-content: space-between;
  gap: ${({ editorial, layoutStatistics }) => (editorial ? '100px' : layoutStatistics ? '80px' : '60px')};
  max-width: ${({ layoutStatistics, editorial }) => (layoutStatistics ? editorial ? '' : '1280px' : '')};

  @media screen and (max-width: ${media.mediumTop}) {
    flex-direction: ${({ altMobileOrder }) => (altMobileOrder ? 'column-reverse' : 'column')};
    align-items: center;
    text-align: center;
    gap: ${({ mobileGap }) => (mobileGap ? `${mobileGap}px` : '60px')};
  }

  //landscape titleset is funky around 960px-1090px if set to row, so we want flex-direction to be column if under 1280px
  @media screen and (max-width: ${media.largeTop}) { 
    flex-direction: ${({ landscape }) => (landscape ? 'column' : '')};
  }
`;

const TextSectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  text-align: left;
  gap: ${({ layoutStatistics }) => (layoutStatistics ? '40px' : '')};

  div {
    @media screen and (max-width: ${media.mediumTop}) {
      max-width: 100%;
    }
  }
  
  p {
    font-size: ${fontSizes.paragraph.large.fontSize};
    line-height: ${fontSizes.paragraph.large.lineHeight};

    @media screen and (max-width: ${media.largeBottom}) {
      font-size: ${fontSizes.paragraph.large.mobile.fontSize};
      line-height: ${fontSizes.paragraph.large.mobile.lineHeight};
    }

    @media screen and (max-width: ${media.mediumTop}) {
      max-width: 100%;
    }
  }
`;

const CTALink = styled(ExternalLink)`
  margin-top: 20px;
`;

const CTALinkInternal = styled(InternalLink)`
  margin-top: 20px;
`;

const ImageContainerStyles = css`
  max-width: ${({ layoutStatistics, editorial }) => (layoutStatistics ? '560px' : editorial ? '500px' : '540px')};
  
  @media screen and (max-width: ${media.mediumTop}) {
    height: auto;
    max-width: 100%;
    width: 100%;
  }
`;

const ImageContainer = styled.div`
  ${ImageContainerStyles}
`;

const ImgContainer = styled.img`
  ${ImageContainerStyles}
`;

const StyledGatsbyImage = styled(GatsbyImage)`
  height: ${({ height }) => (height ? `${height}px` : '')};
  aspect-ratio: ${({ editorial }) => (editorial ? '1/1' : '')};
  width: ${({ layoutStatistics }) => (layoutStatistics ? '560px' : '')};
  aspect-ratio: ${({ layoutStatistics }) => (layoutStatistics ? '1 / 1' : '')};

  @media screen and (max-width: ${media.mediumTop}) {
    height: auto;
    max-width: 100%;
    width: 100%;
  }
`;


const ImagePreview = styled.img`
  object-fit: contain;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-wrap: no-wrap;
  margin-top: 24px;
  gap: 24px;

  @media screen and (max-width: ${media.mediumBottom}) {
    gap: 16px;
    margin-top: 20px;
  }
`;

const FooterButton = styled(InternalLink)`
  width: fit-content;
  max-height: fit-content;
  padding: 15px;
`;

export default ImageWithTitleset;
